import { useRef, useState } from 'react';
import { MAX_STATEMENT_BYTES, type UploadStatementInput } from '@accounting/shared';
import { openStatement, useStatements, useUploadStatement } from '../../hooks/useStatements';
import { ApiError } from '../../lib/api';
import { formatAccountingDate, formatCurrency } from '../../lib/formatters';

interface Props {
  companyId: string;
  bankAccountId: string;
  reconciliationId?: string;
  /** Defaults for a new upload, taken from the reconciliation. */
  defaultStatementDate?: string;
  defaultEndingBalance?: string;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

export function StatementsPanel({
  companyId,
  bankAccountId,
  reconciliationId,
  defaultStatementDate,
  defaultEndingBalance,
}: Props) {
  // Inside a reconciliation page we only want to see statements linked to
  // THIS reconciliation — otherwise the list grows every month and confuses
  // both the user ("which one is the December statement?") and the AI clear
  // button (which used to pick the most-recent linked PDF — fine when there's
  // one per recon, misleading when historical statements all show up here).
  // Standalone use of the panel (bank-accounts page, etc.) still sees all
  // statements for the bank account.
  const { data: statements = [] } = useStatements(
    companyId,
    reconciliationId ? { reconciliationId } : { bankAccountId },
  );
  const upload = useUploadStatement(companyId, bankAccountId);
  const fileInput = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const uploadFile = async (file: File) => {
    setLocalError(null);
    if (file.size > MAX_STATEMENT_BYTES) {
      setLocalError(`"${file.name}" is too large (max ${MAX_STATEMENT_BYTES / 1024 / 1024} MB).`);
      return;
    }
    try {
      const content = await fileToBase64(file);
      const input: UploadStatementInput = {
        statementDate: defaultStatementDate ?? new Date().toISOString().slice(0, 10),
        fileName: file.name,
        mimeType: file.type || 'application/octet-stream',
        content,
        statementEndingBalance: defaultEndingBalance ?? undefined,
        reconciliationId: reconciliationId ?? undefined,
      };
      upload.mutate(input);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Could not read file');
    }
  };

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    for (const f of files) await uploadFile(f);
  };
  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    for (const f of Array.from(e.dataTransfer.files ?? [])) await uploadFile(f);
  };

  const uploadErr =
    upload.error instanceof ApiError
      ? (upload.error.body as { error?: string } | null)?.error ?? 'Upload failed'
      : upload.error?.message;
  const notConfigured = upload.error instanceof ApiError && upload.error.status === 503;

  return (
    <div
      className="card"
      style={{
        marginTop: 16,
        padding: 16,
        outline: dragging ? '2px dashed var(--primary)' : undefined,
        outlineOffset: dragging ? '-6px' : undefined,
      }}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
      onDrop={onDrop}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 style={{ fontSize: 16, margin: 0 }}>Bank statements</h2>
        <button className="btn-secondary" onClick={() => fileInput.current?.click()} disabled={upload.isPending}>
          {upload.isPending ? 'Uploading…' : '+ Upload statement'}
        </button>
        <input ref={fileInput} type="file" accept=".pdf,.csv,.jpg,.jpeg,.png,.tif,.tiff" style={{ display: 'none' }} onChange={onPick} />
      </div>
      <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
        {dragging ? 'Drop the statement to upload…' : 'Drag & drop or upload the bank statement (PDF/CSV/image). Stored immutably in Azure.'}
      </p>

      {notConfigured && (
        <div className="field-error" style={{ marginBottom: 8 }}>
          Statement storage isn’t provisioned yet. Run the setup script to create the
          <code> accountingstatements</code> account, then set <code>STATEMENTS_STORAGE_ACCOUNT_NAME</code>.
        </div>
      )}
      {!notConfigured && (localError || uploadErr) && (
        <div className="field-error" style={{ marginBottom: 8 }}>{localError ?? uploadErr}</div>
      )}

      {statements.length === 0 ? (
        <p className="muted" style={{ fontSize: 13, marginBottom: 0 }}>No statements uploaded for this account.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Statement</th>
              <th>Date</th>
              <th style={{ textAlign: 'right' }}>Ending balance</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {statements.map((s) => (
              <tr key={s.id}>
                <td>
                  {s.fileName}
                  {s.uploadStatus === 'mutable' && (
                    <span className="tag warn" style={{ marginLeft: 6 }} title="Immutability not enabled on the storage account">
                      not locked
                    </span>
                  )}
                </td>
                <td>{formatAccountingDate(s.statementDate)}</td>
                <td style={{ textAlign: 'right' }}>{s.statementEndingBalance ? formatCurrency(s.statementEndingBalance) : '—'}</td>
                <td>
                  <button className="btn-secondary" onClick={() => void openStatement(companyId, s.id)}>View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
